import React, { useMemo, useCallback } from "react";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Button,
  Input,
} from "reactstrap";

const DIHeaderBar = ({
  context,
  onFileNew,
  onFileExit,
  onViewResetLayout,
  onImportExcel,
  onToolsSettings,
  onHelpAbout,
  onCloseOp,
  onCloseJob,
  onOpenAuditLog,
  canViewAuditLog = false,
  closeOpDisabled,
  closeJobDisabled,
}) => {
  // Job metadata as SEGMENTS rendered on a dedicated full-width row
  // below the menus — the previous single right-aligned string got
  // ellipsized ("Created By En...") as soon as the legacy fields
  // (Quantity / MAFIA Rev / Engineer) were restored. A row of key:value
  // segments wraps instead of truncating, so everything stays readable.
  const metaSegments = useMemo(() => {
    const mode =
      context?.inputOption === "1"
        ? "Job"
        : context?.inputOption === "2"
        ? "PO\\Line"
        : context?.inputOption === "3"
        ? "Job Lot"
        : context?.inputOption === "4"
        ? "Part Number" // Part Number workflow: never shown as a fake Job/PO
        : "";
    const isPartOnly = context?.inputOption === "4";
    const segs = [];
    // Round DI-Country — the ACTIVE database first, so the country of the
    // open workspace is always visible in the inspection header (spec:
    // "the active inspection header should make the country visible").
    if (context?.country)
      segs.push({
        k: "Database",
        v: context.country === "INDIA" ? "India" : "USA",
      });
    // Part Number mode: the typed value IS the part — one "Part Number"
    // segment (no duplicate "Part" segment, no ASM segment, no fake Job).
    if (context?.inputJob)
      segs.push({ k: mode || "Job", v: String(context.inputJob) });
    if (!isPartOnly && context?.assembly !== "" && context?.assembly != null)
      segs.push({ k: "ASM", v: String(context.assembly) });
    if (!isPartOnly && context?.part)
      segs.push({ k: "Part", v: String(context.part) });
    // Round 20 — Part Number mode: which inspection run is open
    // (Lot 0 = Inspection #1). Only shown in part-only mode; job/PO/lot
    // modes keep their existing segments untouched.
    if (isPartOnly && context?.lotNum != null)
      segs.push({ k: "Inspection #", v: String(Number(context.lotNum) + 1) });
    if (context?.waveNumber)
      segs.push({ k: "Wave", v: String(context.waveNumber) });
    // Legacy MDIParent1 status-strip fields (verbatim labels):
    if (context?.quantity != null && Number(context.quantity) > 0)
      segs.push({ k: "Quantity", v: String(context.quantity) });
    if (context?.revMajor != null && context?.revMinor != null)
      segs.push({ k: "MAFIA Rev", v: `${context.revMajor}-${context.revMinor}` });
    // Round DI-1 — "Created By Engineer" removed (owner: it made no sense
    // here and was showing incorrect data). Round DI-1b: the guard `if`
    // that used to wrap the removed push is gone too — left bare, it had
    // captured the `return segs;` below as its body, so the memo returned
    // undefined whenever no engineer was set and .length crashed the page.
    return segs;
  }, [
    context?.country, // Round DI-Country
    context?.inputOption,
    context?.inputJob,
    context?.assembly,
    context?.part,
    context?.waveNumber,
    context?.quantity,
    context?.revMajor,
    context?.revMinor,
    context?.lotNum,
    context?.engineer,
  ]);

  const dnaDisplayValue = useMemo(
    () => context?.dnaDisplay || "",
    [context?.dnaDisplay]
  );

  const handleFileNew = useCallback(() => onFileNew?.(), [onFileNew]);
  const handleFileExit = useCallback(() => onFileExit?.(), [onFileExit]);
  const handleImportExcel = useCallback(() => onImportExcel?.(), [onImportExcel]);
  // Round 1: retained for the removed Close Op/Job menu (restore-ready).
  // eslint-disable-next-line no-unused-vars
  const handleCloseOp = useCallback(() => onCloseOp?.(), [onCloseOp]);
  // eslint-disable-next-line no-unused-vars
  const handleCloseJob = useCallback(() => onCloseJob?.(), [onCloseJob]);

  return (
    <div className="di-header-bar">
      <div className="di-header-inner">
        <div className="di-header-left">
          <UncontrolledDropdown className="di-menu-item">
            <DropdownToggle
              caret
              color="link"
              className="di-menu-toggle"
            >
              File
            </DropdownToggle>
            <DropdownMenu className="di-menu-dropdown">
              <DropdownItem onClick={handleFileNew}>New / Clear</DropdownItem>
              <DropdownItem divider />
              <DropdownItem onClick={handleFileExit}>Exit</DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>

          <UncontrolledDropdown className="di-menu-item">
            <DropdownToggle
              caret
              color="link"
              className="di-menu-toggle"
            >
              Import
            </DropdownToggle>
            <DropdownMenu className="di-menu-dropdown">
              <DropdownItem onClick={handleImportExcel}>
                Vision Excel...
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>

          {/* Round 7 — DI Change Log, in-module entry point. Shown only to
              IT Admin and supervisors (the route + API also enforce this). */}
          {canViewAuditLog ? (
            <button
              type="button"
              className="di-menu-toggle btn btn-link"
              style={{ textDecoration: "none" }}
              onClick={() => onOpenAuditLog?.()}
              title="View the Digital Inspection audit log"
            >
              <i className="ri-file-list-3-line me-1" />
              Change Log
            </button>
          ) : null}

          {/* Round 1 (DI parity): the "Close Op/Job" menu is intentionally
              removed from the UI for now, per product decision. Backend
              endpoints (prepare-close-operation / close-operation /
              prepare-close-job / closejobquality) and the modal components
              (CloseOpModal, FrmSupvOv) are LEFT INTACT and untouched. The
              onCloseOp / onCloseJob props + handlers are retained below so the
              menu can be restored later without other changes. */}
        </div>

        <div className="di-header-right">
{/* job meta moved to its own full-width row below */}

          <div className="di-dna-wrap">
            <span className="di-dna-label">DNA</span>
            <Input disabled value={dnaDisplayValue} className="di-dna-input" />
          </div>
        </div>
      </div>

      {metaSegments.length > 0 && (
        <div className="di-header-meta">
          {metaSegments.map((seg) => (
            <span className="di-meta-seg" key={seg.k}>
              <span className="di-meta-key">{seg.k}:</span>
              <span className="di-meta-val">{seg.v}</span>
            </span>
          ))}
        </div>
      )}

      <style>{`
        .di-header-bar {
          border: 1px solid #e6e6e6;
          border-radius: 8px;
          background: #ffffff;
          width: 100%;
          min-width: 0;
          position: relative;
          /* No explicit z-index here on purpose. A high value (was 1200)
             made this bar paint OVER the app navbar (z-index 1002) and hid
             the profile/logout dropdown. The File/Import menus below use
             their own absolutely-positioned dropdown layer, which only
             needs to sit above page content — not the navbar. */
          overflow: visible !important;
        }

        .di-header-inner {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          min-width: 0;
          padding: 8px 12px;
          position: relative;
          overflow: visible !important;
        }

        .di-header-left {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 0 0 auto;
          min-width: 0;
          overflow: visible !important;
        }

        .di-menu-item {
          position: relative;
          flex: 0 0 auto;
        }

        .di-menu-toggle.btn.btn-link {
          text-decoration: none !important;
          color: #212529 !important;
          font-weight: 600;
          padding: 4px 8px !important;
          border: 0 !important;
          box-shadow: none !important;
          white-space: nowrap;
        }

        .di-menu-toggle.btn.btn-link:hover,
        .di-menu-toggle.btn.btn-link:focus,
        .di-menu-toggle.btn.btn-link:active,
        .di-menu-toggle.btn.btn-link.show {
          color: #0d6efd !important;
          text-decoration: none !important;
          box-shadow: none !important;
        }

        .di-menu-item .dropdown-menu,
        .di-menu-dropdown.dropdown-menu {
          position: absolute !important;
          inset: auto auto auto auto !important;
          top: calc(100% + 4px) !important;
          left: 0 !important;
          transform: none !important;
          z-index: 3000 !important;
          min-width: 180px;
          display: none;
        }

        .di-menu-item.show .dropdown-menu,
        .di-menu-item .dropdown-menu.show {
          display: block !important;
        }

        .di-header-right {
          margin-left: auto;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          flex: 1 1 auto;
          min-width: 0;
        }

        .di-header-meta {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px 18px;
          padding: 6px 14px 10px;
          border-top: 1px dashed #e5e7eb;
        }
        .di-meta-seg {
          display: inline-flex;
          align-items: baseline;
          gap: 5px;
          white-space: nowrap;
        }
        .di-meta-key {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #6b7280;
        }
        .di-meta-val {
          font-size: 14px;
          font-weight: 700;
          color: #1f2937;
          font-variant-numeric: tabular-nums;
        }

        .di-dna-wrap {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 0 0 auto;
        }

        .di-dna-label {
          font-size: 12px;
          color: #6c757d;
          white-space: nowrap;
        }

        .di-dna-input {
          width: 90px;
          height: 30px;
          flex: 0 0 auto;
        }

        .di-import-btn {
          flex: 0 0 auto;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
};

export default React.memo(DIHeaderBar);